/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import CustomTabPanel from "components/CustomTabPanel";
import data from "layouts/dashboard/components/Projects/data";
import useApi from "components/Utils/api";
import toast from "utils/toast";
import { useMaterialUIController } from "context";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import Papa from "papaparse";
import { format } from "date-fns";

function a11yProps(index) {
  return { id: `reports-tab-${index}`, "aria-controls": `reports-tabpanel-${index}` };
}

const tabSx = ({ palette }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  minHeight: "unset",
  py: 2,
  px: 3,
  color: `${palette.grey[500]} !important`,
  "&.Mui-selected": {
    color: `${palette.info.main} !important`,
  },
});

const filterFieldSx = { minWidth: "200px", "& .MuiOutlinedInput-root": { height: "38px" } };
const filterSelectSx = {
  minWidth: "200px",
  "& .MuiOutlinedInput-root": { height: "38px" },
  "& .MuiSelect-select": { fontSize: "0.8rem" },
};
const filterLabelProps = { shrink: true, style: { fontSize: "0.8rem" } };
const filterInputProps = { style: { fontSize: "0.8rem" } };

// ── shared status-pill styles (refund status is the same enum in both the
//    refund-report table and the transaction-details refund dialog result) ──
const REFUND_STATUS_STYLES = {
  SUCCESS: { bg: "#d4edda", color: "#155724" },
  PENDING: { bg: "#fff3cd", color: "#856404" },
  FAILED:  { bg: "#f8d7da", color: "#721c24" },
};

function RefundStatusBadge({ value }) {
  if (!value) return <span>—</span>;
  const s = REFUND_STATUS_STYLES[value] ?? { bg: "#e2e3e5", color: "#383d41" };
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: s.bg,
        color: s.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </MDBox>
  );
}

function CopyCell({ value }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  if (!value) return <span>—</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "default" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={value}
    >
      <span style={{ maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }}>
        {`${value.slice(0, 8)}…`}
      </span>
      <span
        onClick={handleCopy}
        style={{ cursor: "pointer", color: copied ? "#4caf50" : "#9e9e9e", fontSize: "1rem", visibility: hovered ? "visible" : "hidden", flexShrink: 0 }}
      >
        {copied ? "✓" : "⎘"}
      </span>
    </span>
  );
}

const refundReportColumns = [
  {
    Header: "Refund ID",
    accessor: "refundId",
    width: "10%",
    align: "left",
    Cell: ({ value }) => <CopyCell value={value} />,
  },
  {
    Header: "Payment ID",
    accessor: "paymentId",
    width: "10%",
    align: "left",
    Cell: ({ value }) => <CopyCell value={value} />,
  },
  {
    Header: "Merchant Txn ID",
    accessor: "merchantTransactionId",
    width: "12%",
    align: "left",
    Cell: ({ value }) => <CopyCell value={value} />,
  },
  { Header: "Processor", accessor: "processorType", width: "8%", align: "left" },
  {
    Header: "Processor Ref",
    accessor: "processorRefundId",
    width: "10%",
    align: "left",
    Cell: ({ value }) => (value ? <CopyCell value={value} /> : <span>—</span>),
  },
  {
    Header: "Amount",
    accessor: (row) =>
      row.amountInCents != null
        ? `${(parseFloat(row.amountInCents) / 100).toFixed(2)} ${row.currency ?? ""}`.trim()
        : "—",
    width: "9%",
    align: "left",
  },
  { Header: "Currency", accessor: "currency", width: "7%", align: "left" },
  {
    Header: "Status",
    accessor: "status",
    width: "8%",
    align: "left",
    Cell: ({ value }) => <RefundStatusBadge value={value} />,
  },
  {
    Header: "Reason",
    accessor: "reason",
    width: "15%",
    align: "left",
    Cell: ({ value }) => (
      <span title={value} style={{ display: "inline-block", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value ?? "—"}
      </span>
    ),
  },
  {
    Header: "Created Date",
    accessor: "createdDate",
    width: "11%",
    align: "left",
    Cell: ({ value }) => {
      if (!value) return <span>—</span>;
      const d = new Date(value);
      return <span>{isNaN(d) ? value : d.toLocaleString()}</span>;
    },
  },
];

// ── POS transactions ──────────────────────────────────────────────────────────

const POS_STATUS_STYLES = {
  SUCCESS: { bg: "#d4edda", color: "#155724" },
  APPROVED: { bg: "#d4edda", color: "#155724" },
  COMPLETED: { bg: "#d4edda", color: "#155724" },
  PENDING: { bg: "#fff3cd", color: "#856404" },
  FAILED: { bg: "#f8d7da", color: "#721c24" },
  DECLINED: { bg: "#f8d7da", color: "#721c24" },
};

function PosStatusBadge({ value }) {
  if (!value) return <span>—</span>;
  const s = POS_STATUS_STYLES[String(value).toUpperCase()] ?? { bg: "#e2e3e5", color: "#383d41" };
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: s.bg,
        color: s.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </MDBox>
  );
}

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? String(value) : d.toLocaleString();
};

const formatPosAmount = (row) =>
  row.amount != null ? `${Number(row.amount).toFixed(2)} ${row.currency ?? ""}`.trim() : "—";

const posTransactionColumns = [
  { Header: "MID", accessor: "mid", width: "14%", align: "left" },
  { Header: "TID", accessor: "tid", width: "12%", align: "left" },
  { Header: "POS ID", accessor: "posId", width: "12%", align: "left" },
  {
    Header: "Terminal Txn Date",
    accessor: (row) => formatDateTime(row.terminalTransactionDate),
    id: "terminalTransactionDate",
    width: "18%",
    align: "left",
  },
  { Header: "Amount", accessor: formatPosAmount, id: "amount", width: "12%", align: "left" },
  {
    Header: "Status",
    accessor: "status",
    width: "12%",
    align: "left",
    Cell: ({ value }) => <PosStatusBadge value={value} />,
  },
  { Header: "PAN", accessor: "maskedPan", width: "14%", align: "left" },
];

const POS_DETAIL_FIELDS = [
  { label: "Transaction Number", key: "transactionNumber" },
  { label: "ID", key: "id" },
  { label: "RRN", key: "rrn" },
  { label: "Auth Code", key: "authCode" },
  { label: "MID", key: "mid" },
  { label: "TID", key: "tid" },
  { label: "POS ID", key: "posId" },
  { label: "User Name", key: "userName" },
  { label: "Device ID", key: "deviceId" },
  { label: "Serial Number", key: "serialNumber" },
  { label: "Terminal Txn Date", key: "terminalTransactionDate", format: formatDateTime },
  { label: "Transaction Date", key: "transactionDate", format: formatDateTime },
  { label: "Amount", key: "amount", format: (_, row) => formatPosAmount(row) },
  { label: "Status", key: "status", render: (v) => <PosStatusBadge value={v} /> },
  { label: "Settlement Status", key: "settlementStatus" },
  { label: "Response Code", key: "responseCode" },
  { label: "Pay Method", key: "payMethod" },
  { label: "Card Brand", key: "cardBrand" },
  { label: "Masked PAN", key: "maskedPan" },
  { label: "Trade Request No", key: "tradeRequestNo" },
  { label: "Merchant Order No", key: "merchantOrderNo" },
  { label: "Token", key: "token" },
  { label: "Comments", key: "comments" },
];

function PosTransactionDialog({ transaction, onClose }) {
  return (
    <Dialog open={!!transaction} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <MDTypography variant="h6" fontWeight="medium">POS Transaction Details</MDTypography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {transaction &&
          POS_DETAIL_FIELDS.map(({ label, key, format: fmt, render }) => {
            const raw = transaction[key];
            let content;
            if (render) content = render(raw);
            else if (fmt) content = fmt(raw, transaction);
            else content = raw == null || raw === "" ? "—" : String(raw);
            return (
              <MDBox
                key={key}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                py={0.75}
                sx={{ borderBottom: "1px solid", borderColor: "grey.200" }}
              >
                <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ textTransform: "none" }}>
                  {label}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  fontWeight="regular"
                  sx={{ textTransform: "none", textAlign: "right", wordBreak: "break-all" }}
                >
                  {content}
                </MDTypography>
              </MDBox>
            );
          })}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>Close</MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ── refund dialog (triggered from the Transaction Details tab) ─────────────────

function RefundDialog({ open, onClose, transaction, onSubmit, loading }) {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [result, setResult] = useState(null);

  const amountInCents = transaction ? Math.round(parseFloat(transaction.amountInCents ?? 0)) : 0;
  const amountDisplay = transaction
    ? `${(amountInCents / 100).toFixed(2)} ${transaction.currency ?? ""}`.trim()
    : "";

  const handleClose = () => {
    setReason("");
    setReasonError(false);
    setResult(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!reason.trim()) { setReasonError(true); return; }
    onSubmit({ amountInCents, reason: reason.trim() }, (res) => setResult(res));
  };

  const statusStyle = result ? (REFUND_STATUS_STYLES[result.status] ?? REFUND_STATUS_STYLES.PENDING) : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <MDTypography variant="h6" fontWeight="medium">Refund Transaction</MDTypography>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {!result ? (
          <MDBox>
            <MDBox
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: "rgba(26,115,232,0.06)",
                border: "1px solid rgba(26,115,232,0.18)",
              }}
            >
              <MDTypography variant="caption" color="text" sx={{ textTransform: "none", display: "block" }}>
                Refund amount
              </MDTypography>
              <MDTypography variant="h6" color="info" fontWeight="bold" sx={{ mt: 0.25 }}>
                {amountDisplay}
              </MDTypography>
            </MDBox>

            <TextField
              label="Reason"
              fullWidth
              size="small"
              required
              multiline
              rows={3}
              value={reason}
              onChange={(e) => { setReason(e.target.value); setReasonError(false); }}
              error={reasonError}
              helperText={reasonError ? "Reason is required" : ""}
              placeholder="e.g. Customer requested refund"
            />
          </MDBox>
        ) : (
          <MDBox>
            <MDBox
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                backgroundColor: statusStyle.bg,
                border: `1px solid ${statusStyle.color}30`,
              }}
            >
              <MDTypography variant="caption" fontWeight="bold" sx={{ textTransform: "none", color: statusStyle.color, display: "block", mb: 0.5 }}>
                Refund {result.status}
              </MDTypography>
              {result.refundId && (
                <MDTypography variant="caption" color="text" sx={{ textTransform: "none", display: "block" }}>
                  Refund ID: {result.refundId}
                </MDTypography>
              )}
              {result.processorRefundId && (
                <MDTypography variant="caption" color="text" sx={{ textTransform: "none", display: "block" }}>
                  Processor Ref: {result.processorRefundId}
                </MDTypography>
              )}
              <MDTypography variant="caption" color="text" sx={{ textTransform: "none", display: "block" }}>
                Amount: {((result.amountInCents ?? amountInCents) / 100).toFixed(2)} {result.currency ?? transaction?.currency ?? ""}
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <MDButton variant="outlined" color="secondary" onClick={handleClose}>
          {result ? "Close" : "Cancel"}
        </MDButton>
        {!result && (
          <MDButton variant="gradient" color="error" onClick={handleConfirm} disabled={loading}>
            {loading ? "Processing…" : "Confirm Refund"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

function downloadCsv(filename, headers, values) {
  const csv = Papa.unparse([headers, ...values], { quotes: true });
  const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}_${ts}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function Reports() {
  const { merchantTransactionDetailsColumns, merchantTransactionSummaryColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const merchantMid = controller.merchant?.mid;
  const api = useApi();

  const [tabValue, setTabValue] = useState(0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [dateRange, setDateRange] = useState([today, today]);

  // ── Transaction details ──
  const [txnRows, setTxnRows] = useState([]);
  const [txnStatusFilter, setTxnStatusFilter] = useState("All");
  const [txnSearch, setTxnSearch] = useState("");
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const TXN_STATUS_GROUPS = {
    INITIATED: ["INITIATED"],
    APPROVED: ["AUTHORIZED", "CAPTURED", "COMPLETED", "SETTLEMENT_INITIATED", "SETTLED"],
    FAILED: ["FAILED", "VOID", "VOID_UNSUCCESSFULL"],
  };

  const filteredTxnRows = useMemo(() => {
    const query = txnSearch.trim().toLowerCase();
    return txnRows.filter((r) => {
      if (txnStatusFilter !== "All" && !(TXN_STATUS_GROUPS[txnStatusFilter] || []).includes(r.status)) return false;
      if (query) {
        const haystack = Object.values(r)
          .map((v) => (v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v)))
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [txnRows, txnStatusFilter, txnSearch]);

  const refundColumn = {
    Header: "Refund",
    id: "refund-action",
    accessor: (row) => row.canRefund,
    width: "6%",
    align: "center",
    Cell: ({ row }) =>
      row.original.canRefund ? (
        <MDButton
          size="small"
          variant="outlined"
          color="error"
          sx={{ py: 0.3, px: 1.2, fontSize: "0.72rem", minWidth: 0, textTransform: "none" }}
          onClick={(e) => { e.stopPropagation(); setRefundTarget(row.original); }}
        >
          Refund
        </MDButton>
      ) : null,
  };

  const txnTableColumns = useMemo(
    () => [...merchantTransactionDetailsColumns, refundColumn],
    [merchantTransactionDetailsColumns]
  );

  const fetchTxnDetails = (range) => {
    if (!range || range.length !== 2) return;
    api
      .post("transaction-details", {
        startDate: format(range[0], "MM/dd/yyyy"),
        endDate: format(range[1], "MM/dd/yyyy"),
        status: null,
      })
      .then((res) => setTxnRows(res.data?.data ?? []))
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setTxnRows([]);
      });
  };

  const handleDownloadTxnCSV = () => {
    if (filteredTxnRows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = merchantTransactionDetailsColumns.map((c) => c.Header);
    const values = filteredTxnRows.map((row) =>
      merchantTransactionDetailsColumns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    downloadCsv("TransactionDetails", headers, values);
  };

  const handleRefund = ({ amountInCents, reason }, onResult) => {
    if (!refundTarget?.paymentId) return;
    setRefunding(true);
    api
      .post(`payments/${refundTarget.paymentId}/refund`, { amountInCents, reason })
      .then((res) => {
        const result = res.data?.data ?? res.data;
        onResult(result);
        if (result?.status === "SUCCESS") {
          toast.success("Refund submitted successfully.");
        } else if (result?.status === "PENDING") {
          toast.success("Refund is pending processing.");
        } else {
          toast.error("Refund failed.");
        }
        fetchTxnDetails(dateRange);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to submit refund.");
        }
      })
      .finally(() => setRefunding(false));
  };

  // ── Transaction summary ──
  const [summaryRows, setSummaryRows] = useState([]);

  const fetchSummary = (range) => {
    if (!range || range.length !== 2) return;
    api
      .post("transaction-summary", {
        startDate: format(range[0], "MM/dd/yyyy"),
        endDate: format(range[1], "MM/dd/yyyy"),
      })
      .then((res) => {
        const body = res.data?.data ?? {};
        setSummaryRows(body.transactionSummary ?? []);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setSummaryRows([]);
      });
  };

  const handleDownloadSummaryCSV = () => {
    if (summaryRows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = merchantTransactionSummaryColumns.map((c) => c.Header);
    const values = summaryRows.map((row) =>
      merchantTransactionSummaryColumns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    downloadCsv("TransactionSummary", headers, values);
  };

  // ── Refund report ──
  const [refundRows, setRefundRows] = useState([]);
  const [refundStatusFilter, setRefundStatusFilter] = useState("All");
  const [paymentIdInput, setPaymentIdInput] = useState("");

  const filteredRefundRows = useMemo(() => {
    return refundRows.filter((r) => {
      if (refundStatusFilter !== "All" && r.status !== refundStatusFilter) return false;
      return true;
    });
  }, [refundRows, refundStatusFilter]);

  const fetchRefunds = (range) => {
    if (!range || range.length !== 2) return;
    api
      .post("refund-report", {
        startDate: format(range[0], "MM/dd/yyyy") + " 00:00:00",
        endDate: format(range[1], "MM/dd/yyyy") + " 23:59:59",
        paymentId: paymentIdInput.trim() || null,
        status: null,
        processorType: null,
      })
      .then((res) => setRefundRows(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setRefundRows([]);
      });
  };

  const handleDownloadRefundCSV = () => {
    if (filteredRefundRows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = refundReportColumns.map((c) => c.Header);
    const values = filteredRefundRows.map((row) =>
      refundReportColumns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    downloadCsv("RefundReport", headers, values);
  };

  // ── POS transactions ──
  const [posRows, setPosRows] = useState([]);
  const [posSearch, setPosSearch] = useState("");
  const [posDetail, setPosDetail] = useState(null);

  const filteredPosRows = useMemo(() => {
    const query = posSearch.trim().toLowerCase();
    if (!query) return posRows;
    return posRows.filter((r) =>
      Object.values(r)
        .map((v) => (v == null ? "" : String(v)))
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [posRows, posSearch]);

  const fetchPosTransactions = (range) => {
    if (!range || range.length !== 2 || !merchantMid) return;
    api
      .get("pos/transactions", {
        params: {
          mid: merchantMid,
          fromDate: format(range[0], "yyyy-MM-dd"),
          toDate: format(range[1], "yyyy-MM-dd"),
        },
      })
      .then((res) => {
        const body = Array.isArray(res.data) ? res.data : res.data?.data;
        setPosRows(Array.isArray(body) ? body : []);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setPosRows([]);
      });
  };

  const handleDownloadPosCSV = () => {
    if (filteredPosRows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = POS_DETAIL_FIELDS.map((f) => f.label);
    const values = filteredPosRows.map((row) =>
      POS_DETAIL_FIELDS.map(({ key, format: fmt }) => (fmt ? fmt(row[key], row) : row[key] ?? ""))
    );
    downloadCsv("PosTransactions", headers, values);
  };

  // ── shared date range: only the active tab is fetched; switching tabs fetches
  //    that tab for the current range so it never shows stale data ──
  const handleDateRangeChange = (range) => {
    if (!range || range.length !== 2) return;
    setDateRange(range);
  };

  const TAB_FETCHERS = [fetchTxnDetails, fetchRefunds, fetchPosTransactions, fetchSummary];

  useEffect(() => {
    TAB_FETCHERS[tabValue]?.(dateRange);
    // POS depends on the merchant MID, which may hydrate after first render
  }, [tabValue, dateRange, tabValue === 2 ? merchantMid : null]); // eslint-disable-line

  const handleExport = () => {
    if (tabValue === 0) handleDownloadTxnCSV();
    else if (tabValue === 1) handleDownloadRefundCSV();
    else if (tabValue === 2) handleDownloadPosCSV();
    else handleDownloadSummaryCSV();
  };

  const [activeCount, activeLabel] = [
    [txnRows.length, "transaction"],
    [refundRows.length, "refund"],
    [posRows.length, "POS transaction"],
    [summaryRows.length, "summary row"],
  ][tabValue];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox mb={3} display="flex" alignItems="center">
              <MDBox flex={1}>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  Reports
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  {activeCount} {activeLabel}{activeCount === 1 ? "" : "s"}
                </MDTypography>
              </MDBox>
              <DateRangePicker
                shouldDisableDate={afterToday()}
                ranges={predefinedRanges}
                value={dateRange}
                onChange={handleDateRangeChange}
                format="dd MMMM yyyy"
              />
              <MDBox flex={1} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="secondary" size="small" onClick={handleExport}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">download</Icon>
                  Export CSV
                </MDButton>
              </MDBox>
            </MDBox>

            {/* ── tabs (outside the card) ── */}
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              sx={{
                minHeight: "unset",
                width: "fit-content",
                backgroundColor: "transparent !important",
                borderRadius: 0,
                p: 0,
                mb: 2,
                borderBottom: "1px solid",
                borderColor: "grey.300",
                "& .MuiTabs-indicator": {
                  height: "2px",
                  borderRadius: 0,
                  boxShadow: "none",
                  backgroundColor: "info.main",
                },
              }}
            >
              <Tab label="Transaction details" sx={tabSx} {...a11yProps(0)} />
              <Tab label="Refund report" sx={tabSx} {...a11yProps(1)} />
              <Tab label="POS transactions" sx={tabSx} {...a11yProps(2)} />
              <Tab label="Transaction summary" sx={tabSx} {...a11yProps(3)} />
            </Tabs>

            <Card
              sx={({ boxShadows }) => ({
                border: "1px solid",
                borderColor: "grey.300",
                boxShadow: boxShadows.sm,
                borderRadius: "12px",
                overflow: "hidden",
              })}
            >
              {/* ── Tab 0: Transaction details ── */}
              <CustomTabPanel value={tabValue} index={0} contentSx={{ p: 0 }}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  flexWrap="wrap"
                  px={2.25}
                  py={1.25}
                  bgColor="grey-100"
                  sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
                >
                  <TextField
                    label="Search"
                    placeholder="Search all fields"
                    size="small"
                    variant="outlined"
                    InputLabelProps={filterLabelProps}
                    InputProps={filterInputProps}
                    value={txnSearch}
                    onChange={(e) => setTxnSearch(e.target.value)}
                    sx={filterFieldSx}
                  />

                  <TextField
                    select
                    label="Status"
                    size="small"
                    variant="outlined"
                    InputLabelProps={filterLabelProps}
                    InputProps={filterInputProps}
                    value={txnStatusFilter}
                    onChange={(e) => setTxnStatusFilter(e.target.value)}
                    sx={filterSelectSx}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="INITIATED">Initiated</MenuItem>
                    <MenuItem value="APPROVED">Approved</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                  </TextField>
                </MDBox>

                <MDBox px={2.25} py={2}>
                  <DataTable
                    table={{ columns: txnTableColumns, rows: filteredTxnRows }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                    hideExpandColumn
                    entriesPerPage
                    entriesPerPagePosition="bottom"
                  />
                </MDBox>
              </CustomTabPanel>

              {/* ── Tab 1: Refund report ── */}
              <CustomTabPanel value={tabValue} index={1} contentSx={{ p: 0 }}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  flexWrap="wrap"
                  px={2.25}
                  py={1.25}
                  bgColor="grey-100"
                  sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
                >
                  <TextField
                    label="Payment ID"
                    placeholder="Search by payment ID"
                    size="small"
                    variant="outlined"
                    InputLabelProps={filterLabelProps}
                    InputProps={filterInputProps}
                    value={paymentIdInput}
                    onChange={(e) => setPaymentIdInput(e.target.value)}
                    sx={filterFieldSx}
                  />

                  <TextField
                    select
                    label="Status"
                    size="small"
                    variant="outlined"
                    InputLabelProps={filterLabelProps}
                    InputProps={filterInputProps}
                    value={refundStatusFilter}
                    onChange={(e) => setRefundStatusFilter(e.target.value)}
                    sx={filterSelectSx}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                  </TextField>
                </MDBox>

                {refundRows.length > 0 && (
                  <MDBox
                    display="flex"
                    gap={2}
                    flexWrap="wrap"
                    px={2.25}
                    py={1.5}
                    sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
                  >
                    <MDTypography variant="caption" color="text">
                      Total: <strong>{filteredRefundRows.length}</strong> refunds
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Success:{" "}
                      <strong style={{ color: "#155724" }}>
                        {filteredRefundRows.filter((r) => r.status === "SUCCESS").length}
                      </strong>
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Pending:{" "}
                      <strong style={{ color: "#856404" }}>
                        {filteredRefundRows.filter((r) => r.status === "PENDING").length}
                      </strong>
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      Failed:{" "}
                      <strong style={{ color: "#721c24" }}>
                        {filteredRefundRows.filter((r) => r.status === "FAILED").length}
                      </strong>
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox px={2.25} py={2}>
                  <DataTable
                    table={{ columns: refundReportColumns, rows: filteredRefundRows }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                    hideExpandColumn
                    entriesPerPage
                    entriesPerPagePosition="bottom"
                  />
                </MDBox>
              </CustomTabPanel>

              {/* ── Tab 2: POS transactions ── */}
              <CustomTabPanel value={tabValue} index={2} contentSx={{ p: 0 }}>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  flexWrap="wrap"
                  px={2.25}
                  py={1.25}
                  bgColor="grey-100"
                  sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
                >
                  <TextField
                    label="Search"
                    placeholder="Search all fields"
                    size="small"
                    variant="outlined"
                    InputLabelProps={filterLabelProps}
                    InputProps={filterInputProps}
                    value={posSearch}
                    onChange={(e) => setPosSearch(e.target.value)}
                    sx={filterFieldSx}
                  />
                </MDBox>

                <MDBox px={2.25} py={2}>
                  <DataTable
                    table={{ columns: posTransactionColumns, rows: filteredPosRows }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                    hideExpandColumn
                    entriesPerPage
                    entriesPerPagePosition="bottom"
                    disableRowDialog
                    onRowClicked={(row) => setPosDetail(row.original)}
                  />
                </MDBox>
              </CustomTabPanel>

              {/* ── Tab 3: Transaction summary ── */}
              <CustomTabPanel value={tabValue} index={3} contentSx={{ p: 0 }}>
                <MDBox px={2.25} py={2}>
                  <DataTable
                    table={{ columns: merchantTransactionSummaryColumns, rows: summaryRows }}
                    showTotalEntries
                    isSorted
                    noEndBorder
                    hideExpandColumn
                    entriesPerPage
                    entriesPerPagePosition="bottom"
                  />
                </MDBox>
              </CustomTabPanel>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <RefundDialog
        open={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        transaction={refundTarget}
        onSubmit={handleRefund}
        loading={refunding}
      />

      <PosTransactionDialog transaction={posDetail} onClose={() => setPosDetail(null)} />
    </DashboardLayout>
  );
}

export default Reports;
